import { useState, useEffect, useRef, useCallback } from 'react';

const SCROLL_POSITION_KEY = 'chatScrollPosition';
const SCROLL_RESTORE_DELAY = 50; // 延迟恢复滚动位置，确保 DOM 已更新
const SCROLL_CLEAR_DELAY = 1200; // 延迟清除会话滚动位置键，避免后续自动滚动覆盖恢复位置

export function useScrollManagement() {
    const [showScrollButton, setShowScrollButton] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const shouldRestoreScroll = useRef(false);
    const hasRestoredScroll = useRef(false);

    // 保存滚动位置到 sessionStorage
    const saveScrollPosition = useCallback(() => {
        console.log('saveScrollPosition');
        
        if (containerRef.current) {
            const scrollPosition = containerRef.current.scrollTop;
            sessionStorage.setItem(SCROLL_POSITION_KEY, scrollPosition.toString());
        }
    }, []);

    // 确保 scrollToBottom 函数稳定
    const scrollToBottom = useCallback(() => {
        const hasSaved = sessionStorage.getItem(SCROLL_POSITION_KEY) !== null;
        if (hasSaved) return; // 有恢复位置或在抑制时间内，不滚到底部
        requestAnimationFrame(() => {
            if (containerRef.current) {
                containerRef.current.scrollTo({
                    top: containerRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        });
    }, []); // 空依赖确保函数稳定

    // 初始化时：如果存在保存的位置则优先恢复并立刻清除；否则滚动到底部
    useEffect(() => {
        const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
        if (savedPosition !== null) {
            const position = parseInt(savedPosition, 10);
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollTop = position;
                }
                // 再尝试一次，避免内容异步渲染导致偏移
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = position;
                    }
                }, 300);
                // 延迟清除，确保后续异步 scrollToBottom 不会覆盖恢复位置
                setTimeout(() => {
                    sessionStorage.removeItem(SCROLL_POSITION_KEY);
                }, SCROLL_CLEAR_DELAY);
            }, SCROLL_RESTORE_DELAY);
        } else {
            scrollToBottom();
        }
    }, [scrollToBottom]); // 依赖项是函数引用，不会为 undefined

    // 添加滚动事件监听器
    useEffect(() => {
        const handleScroll = () => {

            if (containerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                const isAtBottom = scrollHeight - scrollTop <= clientHeight + 10;
                setShowScrollButton(!isAtBottom);
            }
        };

        const currentContainer = containerRef.current;
        if (currentContainer) {
            currentContainer.addEventListener('scroll', handleScroll);
            // 初始检查一次滚动位置
            handleScroll();
        }

        return () => {
            if (currentContainer) {
                currentContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, []); // 空依赖数组，只在挂载和卸载时运行


    return {
        showScrollButton,
        containerRef,
        scrollToBottom,
        saveScrollPosition,
    };
}
